import { useEffect, useState } from 'react';
import { Button, TextField, Grid, Fab, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Alert, Autocomplete } from '@mui/material';
import clsx from 'clsx';
import { LoadingPage, ButtonLoading, Icons } from 'components';
import { useCopy } from 'hooks';
import OperationDialog from './OperationDialog';
import InvoiceServices from '../services/InvoiceServices';
import createToast from 'components/Toast/Toast';
import { ActionTypes } from './Columns';
import useCustomHook from '../hooks';

export const CommonDialog = ({ open, setOpen, title, content, onConfirm, loading }) => {
  const classes = useStyle();
  return (
    <OperationDialog title={title} open={open} setOpen={setOpen}>
      <div className={classes.container}>
        <Alert severity="error">
          <h3 className={classes.title}>توجه</h3>
          <div className={classes.textWrapper}>{content}</div>
        </Alert>
        <Grid
          md={12}
          xs={12}
          lg={12}
          style={{ display: 'flex', flexDirection: 'row-reverse', marginTop: '16px', gap: '15px' }}
        >
          <ButtonLoading
            name="باشه"
            color="primary"
            variant="contained"
            loading={loading}
            onClick={onConfirm}
            className={classes.button}
          />
        </Grid>
      </div>
    </OperationDialog>
  );
};

export const SharePaymentLinkDialog = ({ open, setOpen, invoice, otherData = null }) => {
  const { copyItem } = useCopy();
  const {
    loading,
    setLoading,
    link,
    setLink,
    hasProfile,
    setHasProfile,
    paymentLinkShare,
    setPaymentLinkShare,
    redirectUri,
    setRedirectUri,
  } = useCustomHook();
  const classes = useStyle();

  const hint = 'لینک پرداخت';

  useEffect(() => {
    if (redirectUri)
      setPaymentLinkShare(
        `${link}&${hasProfile ? 'RedirectURI' : 'redirectUri'}=${encodeURIComponent(redirectUri)}`,
      );
  }, [link, redirectUri]);

  const getShareLink = async () => {
    setLoading(true);
    try {
      const result = await InvoiceServices.getInvoice(invoice.invoiceId);
      if (result.count > 0) {
        try {
          const res = await InvoiceServices.paymentLink(result.result[0].uniqueNumber);
          setHasProfile(res?.data?.result?.hasProfile);
          setLink(res?.data?.result?.link);
        } catch (error) {
          setOpen(false);
        }
      } else {
        createToast('خطا', 'فاکتور مورد نظر یافت نشد', 'danger');
        setOpen(false);
      }
    } catch (error) {
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const shareLink = (url) => window.open(url, '_blank');
  const telegramShare = () =>
    shareLink(`https://telegram.me/share/url?url=${paymentLinkShare}&text=${hint}`);

  const whatsappShare = () => shareLink(`https://wa.me/send?text=${paymentLinkShare}`);

  const gmailShare = () =>
    shareLink(
      `https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=&su=${hint}&body=${paymentLinkShare}&ui=2&tf=1&pli=1`,
    );

  useEffect(() => {
    getShareLink();
  }, [invoice]);

  return (
    <OperationDialog title="اشتراک گذاری لینک پرداخت" open={open} setOpen={setOpen}>
      {loading ? (
        <LoadingPage width="20%" height="20%" style={{ transform: 'scale(1)' }} />
      ) : (
        <>
          <Grid alignItems="center" container>
            <Grid xs={12} item style={{ marginBottom: 5 }}>

              <Grid
                className={classes.dangerBox}
                container
                direction="row"
                alignItems="flex-start"
                justifyContent="flex-start"
              >
                <Grid item>
                  <Icons icon="WarningTriangle" color="#E03E1A" size="28" />
                </Grid>
                <Grid item xs>
                  <Typography className={classes.dangerText}>
                    <h3 className={classes.title}>توجه</h3>

                    درصورت عدم تائید آدرس بازگشت توسط تیم پشتیبانی این لینک به درستی کار نخواهد کرد.

                  </Typography>
                </Grid>
              </Grid>

            </Grid>

            <Grid xs={12} item style={{ marginBottom: 5 }}>
              <Autocomplete
                selectOnFocus
                onChange={(event, newValue) => {
                  setRedirectUri(newValue?.url);
                }}
                id="comparativeAccount"
                isOptionEqualToValue={(option, value) => option.url === value.url}
                getOptionLabel={(option) => option.url}
                options={otherData}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" label="یک آدرس  بازگشت انتخاب کنید" />
                )}
              />
            </Grid>

            <Grid xs={12} item style={{ marginBottom: 5 }}>
              <TextField
                style={{ direction: 'ltr', textAlign: 'center' }}
                fullWidth
                variant="outlined"
                value={paymentLinkShare || link}
                disabled
              />
            </Grid>

            <Grid xs={12} item>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => copyItem(paymentLinkShare || link)}
              >
                کپی کردن لینک
              </Button>
            </Grid>
          </Grid>

          {redirectUri && (
            <Grid className={classes.shareLinks}>
              <Tooltip title="اشتراگ گذاری لینک با تلگرام">
                <Fab
                  onClick={telegramShare}
                  className={clsx(classes.shareLink, classes.telegramIcon)}
                >
                  <span className={classes.extendedIcon}>
                    <Icons icon="telegram" color="#676767ff" size="25" />
                  </span>
                </Fab>
              </Tooltip>
              <Tooltip title="اشتراگ گذاری لینک با واتس اپ">
                <Fab
                  onClick={whatsappShare}
                  className={clsx(classes.shareLink, classes.whatsAppIcon)}
                >
                  <span className={classes.extendedIcon}>
                    <Icons icon="whatsapp" color="#676767ff" size="25" />
                  </span>
                </Fab>
              </Tooltip>
              <Tooltip title="اشتراگ گذاری لینک با جیمیل">
                <Fab onClick={gmailShare} className={clsx(classes.shareLink, classes.emailIcon)}>
                  <span className={classes.extendedIcon}>
                    <Icons icon="email" color="#676767ff" size="25" />
                  </span>
                </Fab>
              </Tooltip>
            </Grid>
          )}
        </>
      )}
    </OperationDialog>
  );
};

export const CancelInvoiceDialog = ({ open, setOpen, invoice, tableRef }) => {
  const [loading, setLoading] = useState(false);
  const cancelInvoice = async () => {
    setLoading(true);
    try {
      if (Array.isArray(invoice)) {
        await InvoiceServices.cancelInvoiceList({ id: invoice.map((item) => item.invoiceId) });
      } else {
        await InvoiceServices.cancelInvoice(invoice.invoiceId);
      }
    } catch (error) {
    } finally {
      setLoading(false);
      setOpen(false);
      setTimeout(() => {
        tableRef?.current?.onChangeTable(true);
      }, 100);
    }
  };
  return (
    <CommonDialog
      title="کنسل کردن فاکتور"
      open={open}
      setOpen={setOpen}
      content={
        <>
          در صورت کنسل کردن فاکتور دیگر نمی توان وضعیت آن را تغییر داد. همچنین در صورت نیاز بر اساس
          قوانین بانک مرکزی هزینه کارمزد از حساب تفصیلی
          {invoice?.guildName ? <b>"{invoice?.guildName}" </b> : ' '}
          کسر خواهد شد.
        </>
      }
      onConfirm={cancelInvoice}
      loading={loading}
    />
  );
};

export const CloseInvoiceDialog = ({ open, setOpen, invoice, tableRef }) => {
  const [loading, setLoading] = useState(false);
  const closeInvoice = async () => {
    setLoading(true);
    try {
      if (Array.isArray(invoice)) {
        await InvoiceServices.closeInvoiceList({ id: invoice.map((item) => item.invoiceId) });
      } else {
        await InvoiceServices.closelInvoice(invoice.invoiceId);
      }
    } catch (error) {
    } finally {
      setLoading(false);
      setOpen(false);
      setTimeout(() => {
        tableRef?.current?.onChangeTable(true);
      }, 100);
    }
  };
  return (
    <CommonDialog
      title="بستن فاکتور"
      open={open}
      setOpen={setOpen}
      content="در صورت بستن فاکتور دیگر نمی توان وضعیت آن را تغییر داد."
      loading={loading}
      onConfirm={closeInvoice}
    />
  );
};

export const PaymentInvoiceDialog = ({ open, setOpen, invoice, tableRef }) => {
  const [loading, setLoading] = useState(false);
  const paymentInvoice = async () => {
    setLoading(true);
    try {
      await InvoiceServices.payment(invoice.invoiceId);
      setTimeout(() => {
        tableRef?.current?.onChangeTable(true);
      }, 100);
    } catch (error) {
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };
  return (
    <CommonDialog
      title="پرداخت (خارج از پلتفرم)"
      open={open}
      setOpen={setOpen}
      content={
        <>با این کار تنها فاکتور پرداخت می شود و نمی توان مبلغ فاکتور را از پلتفرم برداشت کرد.</>
      }
      onConfirm={paymentInvoice}
      loading={loading}
    />
  );
};

export const getDialog = (
  actionType,
  selectedRowData,
  setIsOpenDialog,
  isOpenDialog,
  tableRef,
  otherData,
) => {
  const getComponent = (Component) => (
    <Component
      invoice={selectedRowData}
      setOpen={setIsOpenDialog}
      open={isOpenDialog}
      tableRef={tableRef}
      otherData={otherData}
    />
  );

  switch (actionType) {
    case ActionTypes.CANCEL:
      return getComponent(CancelInvoiceDialog);

    case ActionTypes.CLOSE:
      return getComponent(CloseInvoiceDialog);

    case ActionTypes.SHARE_PAYMENT_LINK:
      return getComponent(SharePaymentLinkDialog);

    case ActionTypes.PAYMENT_OUT_OF_PLATFORM:
      return getComponent(PaymentInvoiceDialog);
    default:
      return '';
  }
};

const useStyle = makeStyles((theme) => ({
  dangerBox: {
    marginTop: theme.spacing(0),
    backgroundColor: '#fad9d161',
    borderRadius: '8px',
    border: 'solid 1px #FAD9D1',
    boxShadow: 'none',
    padding: theme.spacing(1.5),
    margin: '5px 0 0',
    display: 'flex',
    alignItems: 'center',
    '@media(max-width: 550px)': {
      flexDirection: 'column',
    },
    '& h3': {
      fontSize: '15px',

    }
  },
  dangerText: {
    fontSize: '13px',
    lineHeight: '25px',
    paddingLeft: '10px',
    textAlign: 'justify',
    COLOR: '#0C0E10',
    '@media(max-width: 550px)': {
      paddingLeft: '0',
    },
  },
  dangerLink: {
    color: '#ff5500',
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column !important',
  },

  title: { fontSize: '1.5em' },
  textWrapper: {
    marginTop: '1em',
  },
  button: {
    marginTop: '1em',
    width: '100px',
    marginLeft: 'auto',
  },
  shareLinks: {
    marginTop: '1em',
    textAlign: 'center',

  },
  shareLink: {
    marginLeft: '.5em',
    color: 'white',
    width: '40px',
    height: '40px',
    background: '#fff',
    boxShadow: 'none',
    padding: 20,
    border: '2px solid #2b3990',
  },
  extendedIcon: { height: 25 }

}));
